import React, { useState, useEffect, useMemo } from 'react';
import { Container, Card, Row, Col, Spinner, Alert, Table, Button } from 'react-bootstrap';
import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  flexRender,
  createColumnHelper,
  SortingState,
} from '@tanstack/react-table';
import { useLocation, useSearch } from 'wouter';
import StudentSelector from './StudentSelector';
import DateRangePicker from './DateRangePicker';
import { getStudentAttendance, StudentAttendanceData, AttendanceRecord } from '../../services/api';
import { formatRelativeWithDate } from '../../utils/dateUtils';
import { convertToCSV, downloadCSV, generateFilename } from '../../utils/csvUtils';

interface StudentAttendanceReportProps {
  studentId?: string;
}

const StudentAttendanceReport: React.FC<StudentAttendanceReportProps> = ({ studentId }) => {
  const [attendanceData, setAttendanceData] = useState<StudentAttendanceData | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [, setLocation] = useLocation();
  const searchParams = useSearch();

  // Calculate default date range (last 30 days)
  const getDefaultDates = () => {
    const end = new Date();
    const start = new Date();
    start.setDate(end.getDate() - 30);

    return {
      startDate: start.toISOString().split('T')[0],
      endDate: end.toISOString().split('T')[0],
    };
  };

  // Parse query parameters for dates
  const getInitialDates = () => {
    const params = new URLSearchParams(searchParams);
    const fromParam = params.get('from');
    const toParam = params.get('to');

    const defaults = getDefaultDates();

    return {
      startDate: fromParam || defaults.startDate,
      endDate: toParam || defaults.endDate,
    };
  };

  const initialDates = getInitialDates();
  const [startDate, setStartDate] = useState(initialDates.startDate);
  const [endDate, setEndDate] = useState(initialDates.endDate);

  // Update URL when dates change
  const updateDateInURL = (from: string, to: string) => {
    if (!studentId) return;
    const params = new URLSearchParams();
    params.set('from', from);
    params.set('to', to);
    setLocation(`/attendance/student/${studentId}?${params.toString()}`, { replace: true });
  };

  const handleStartDateChange = (date: string) => {
    setStartDate(date);
    updateDateInURL(date, endDate);
  };

  const handleEndDateChange = (date: string) => {
    setEndDate(date);
    updateDateInURL(startDate, date);
  };

  // Fetch attendance data when studentId or dates change
  useEffect(() => {
    const fetchAttendanceData = async () => {
      if (!studentId) return;

      setLoading(true);
      setError(null);

      try {
        const data = await getStudentAttendance(studentId, startDate, endDate);
        setAttendanceData(data);
      } catch (err: any) {
        console.error('Error fetching attendance:', err);
        setError(err.response?.data?.detail || 'Failed to fetch attendance data');
      } finally {
        setLoading(false);
      }
    };

    if (studentId) {
      fetchAttendanceData();
    }
  }, [studentId, startDate, endDate]);


  // Sorting state
  const [sorting, setSorting] = useState<SortingState>([]);

  // Define columns for the table
  const columnHelper = createColumnHelper<AttendanceRecord>();
  const columns = useMemo(
    () => [
      columnHelper.accessor('date', {
        header: 'Date',
        cell: info => info.getValue(),
      }),
      columnHelper.accessor('class_name', {
        header: 'Class Name',
        cell: info => info.getValue(),
      }),
      columnHelper.accessor('class_day', {
        header: 'Day',
        cell: info => info.getValue(),
      }),
      columnHelper.accessor('class_time', {
        header: 'Time',
        cell: info => info.getValue(),
      }),
    ],
    [columnHelper]
  );

  // Create table instance
  const table = useReactTable({
    data: attendanceData?.attendance_records || [],
    columns,
    state: {
      sorting,
    },
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
  });

  // Handle CSV export
  const handleExportCSV = () => {
    if (!attendanceData || attendanceData.attendance_records.length === 0) return;

    const csvHeaders = [
      { key: 'date' as keyof AttendanceRecord, label: 'Date' },
      { key: 'class_name' as keyof AttendanceRecord, label: 'Class Name' },
      { key: 'class_day' as keyof AttendanceRecord, label: 'Day' },
      { key: 'class_time' as keyof AttendanceRecord, label: 'Time' },
    ];

    const csvContent = convertToCSV(attendanceData.attendance_records, csvHeaders);
    const filename = generateFilename(
      `student_${attendanceData.student_name}`,
      startDate,
      endDate
    );

    downloadCSV(csvContent, filename);
  };

  return (
    <Container className="py-4">
      <h1 className="mb-4">Attendance Report by Student{attendanceData && !loading && (<span>: {attendanceData.student_name}</span>)}</h1>

      <Row className="mb-4">
        <Col>
          <StudentSelector selectedStudentId={studentId} />
        </Col>
        <Col>
          <DateRangePicker
            startDate={startDate}
            endDate={endDate}
            onStartDateChange={handleStartDateChange}
            onEndDateChange={handleEndDateChange}
          />
        </Col>
      </Row>


      {studentId && (
        <>
          {loading && (
            <div className="text-center my-5">
              <Spinner animation="border" role="status">
                <span className="visually-hidden">Loading...</span>
              </Spinner>
            </div>
          )}

          {error && (
            <Alert variant="danger" className="my-3">
              {error}
            </Alert>
          )}

          {attendanceData && !loading && (
            <>
              {/* Summary Section */}
              <Row className="mb-4">
                <Col md={4} className="mb-3">
                  <Card className="text-center h-100">
                    <Card.Body>
                      <Card.Title>Total Attendance</Card.Title>
                      <h1 className="display-3">{attendanceData.total_attendance}</h1>
                    </Card.Body>
                  </Card>
                </Col>
                <Col md={4} className="mb-3">
                  <Card className="text-center h-100">
                    <Card.Body>
                      <Card.Title>Last Attendance</Card.Title>
                      <h2 className="display-6 mt-3">
                        {formatRelativeWithDate(attendanceData.last_attendance_date)}
                      </h2>
                    </Card.Body>
                  </Card>
                </Col>
                {attendanceData.class_summaries.map((summary) => (
                  <Col md={4} key={summary.class_name} className="mb-3">
                    <Card className="text-center h-100">
                      <Card.Body>
                        <Card.Title>{summary.class_name}</Card.Title>
                        <h1 className="display-3">{summary.count}</h1>
                      </Card.Body>
                    </Card>
                  </Col>
                ))}
              </Row>

              {/* Table Section */}
              <Card>
                <Card.Body>
                  <div className="d-flex justify-content-between align-items-center mb-3">
                    <Card.Title className="mb-0">Attendance Records</Card.Title>
                    <Button
                      variant="success"
                      size="sm"
                      onClick={handleExportCSV}
                      disabled={!attendanceData || attendanceData.attendance_records.length === 0}
                    >
                      📥 Download CSV
                    </Button>
                  </div>
                  <div style={{ maxHeight: '500px', overflow: 'auto' }}>
                    <Table striped bordered hover variant="dark">
                      <thead style={{ position: 'sticky', top: 0, zIndex: 1 }}>
                        {table.getHeaderGroups().map(headerGroup => (
                          <tr key={headerGroup.id}>
                            {headerGroup.headers.map(header => (
                              <th
                                key={header.id}
                                onClick={header.column.getToggleSortingHandler()}
                                style={{ cursor: 'pointer', userSelect: 'none' }}
                              >
                                {flexRender(header.column.columnDef.header, header.getContext())}
                                {{
                                  asc: ' ▲',
                                  desc: ' ▼',
                                }[header.column.getIsSorted() as string] ?? null}
                              </th>
                            ))}
                          </tr>
                        ))}
                      </thead>
                      <tbody>
                        {table.getRowModel().rows.map(row => (
                          <tr key={row.id}>
                            {row.getVisibleCells().map(cell => (
                              <td key={cell.id}>
                                {flexRender(cell.column.columnDef.cell, cell.getContext())}
                              </td>
                            ))}
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </div>
                </Card.Body>
              </Card>
            </>
          )}
        </>
      )}

      {!studentId && !loading && (
        <Alert variant="info" className="mt-3">
          Please select a student to view their attendance report.
        </Alert>
      )}
    </Container>
  );
};

export default StudentAttendanceReport;
